/**
 * TCB R2 taxonomy resolver — read-only helpers for facility hubs, Finder, Advisor.
 * Does not own package BOM / cart logic.
 */
(function (global) {
  'use strict';

  var cache = {
    taxonomy: null,
    reverse: null,
    loading: null
  };

  function asArray(value) {
    return Array.isArray(value) ? value : [];
  }

  function indexById(list) {
    var map = Object.create(null);
    asArray(list).forEach(function (row) {
      if (row && row.id) map[row.id] = row;
    });
    return map;
  }

  function createApi(taxonomy, reverse) {
    var by = {
      industry: indexById(taxonomy.industries),
      facility: indexById(taxonomy.facilities),
      zone: indexById(taxonomy.zones),
      surface: indexById(taxonomy.surfaces),
      soil: indexById(taxonomy.soils),
      task: indexById(taxonomy.tasks),
      method: indexById(taxonomy.methods),
      goal: indexById(taxonomy.goals),
      package_family: indexById(taxonomy.package_families)
    };

    function resolveAlias(alias) {
      var targets = (reverse && reverse.alias_targets && reverse.alias_targets[alias]) || [];
      return targets[0] || null;
    }

    function facilityByLegacy(legacyId) {
      if (!legacyId) return null;
      if (by.facility['tcb_fac_' + String(legacyId).replace(/-/g, '_')]) {
        return by.facility['tcb_fac_' + String(legacyId).replace(/-/g, '_')];
      }
      var mapped = reverse && reverse.facility_by_legacy && reverse.facility_by_legacy[legacyId];
      return mapped ? by.facility[mapped] || null : null;
    }

    function packageByLegacy(legacyId) {
      var mapped = reverse && reverse.package_by_legacy && reverse.package_by_legacy[legacyId];
      return mapped ? by.package_family[mapped] || null : null;
    }

    function entities(ids, bucket) {
      return asArray(ids)
        .map(function (id) {
          return bucket[id];
        })
        .filter(Boolean);
    }

    function facilityViewModel(facilityRef) {
      var facility =
        typeof facilityRef === 'string'
          ? by.facility[facilityRef] || facilityByLegacy(facilityRef)
          : facilityRef;
      if (!facility) return null;
      return {
        facility: facility,
        industry: by.industry[facility.industry_id] || null,
        zones: entities(facility.zone_ids, by.zone),
        surfaces: entities(facility.surface_ids, by.surface),
        soils: entities(facility.soil_ids, by.soil),
        tasks: entities(facility.task_ids, by.task),
        methods: entities(facility.method_ids, by.method),
        goals: entities(facility.goal_ids, by.goal),
        package_families: entities(facility.package_family_ids, by.package_family),
        seo: {
          title: facility.seo_title || facility.name,
          description: facility.seo_description || '',
          indexable: facility.indexable !== false,
          handle: facility.current_page_handle || null
        }
      };
    }

    return {
      version: taxonomy.version,
      raw: taxonomy,
      reverse: reverse,
      resolveAlias: resolveAlias,
      get: function (type, id) {
        return (by[type] && by[type][id]) || null;
      },
      facilityByLegacy: facilityByLegacy,
      packageByLegacy: packageByLegacy,
      packagesByFacility: function (facilityId) {
        var ids =
          (reverse && reverse.packages_by_facility && reverse.packages_by_facility[facilityId]) || [];
        return entities(ids, by.package_family);
      },
      packagesByTask: function (taskId) {
        var ids = (reverse && reverse.packages_by_task && reverse.packages_by_task[taskId]) || [];
        return entities(ids, by.package_family);
      },
      packagesByMethod: function (methodId) {
        var ids =
          (reverse && reverse.packages_by_method && reverse.packages_by_method[methodId]) || [];
        return entities(ids, by.package_family);
      },
      facilityViewModel: facilityViewModel
    };
  }

  function loadJson(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('Failed to load ' + url + ' (' + res.status + ')');
      return res.json();
    });
  }

  function init(options) {
    options = options || {};
    if (cache.taxonomy && cache.reverse) {
      return Promise.resolve(createApi(cache.taxonomy, cache.reverse));
    }
    if (cache.loading) return cache.loading;
    var taxonomyUrl = options.taxonomyUrl;
    var reverseUrl = options.reverseUrl;
    if (!taxonomyUrl || !reverseUrl) {
      return Promise.reject(new Error('taxonomyUrl and reverseUrl are required'));
    }
    cache.loading = Promise.all([loadJson(taxonomyUrl), loadJson(reverseUrl)]).then(function (pair) {
      cache.taxonomy = pair[0];
      cache.reverse = pair[1];
      cache.loading = null;
      return createApi(cache.taxonomy, cache.reverse);
    });
    return cache.loading;
  }

  function fromData(taxonomy, reverse) {
    cache.taxonomy = taxonomy;
    cache.reverse = reverse || {};
    return createApi(cache.taxonomy, cache.reverse);
  }

  global.TCBTaxonomy = {
    init: init,
    fromData: fromData
  };
})(typeof window !== 'undefined' ? window : globalThis);
